import { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Save,
  Trash2,
  PenLine,
  Calendar,
  Type as TypeIcon,
  FileSignature as SignatureIcon,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import type { DocumentField, DocumentFieldInput, DocumentFieldType } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const fieldTypeMeta: Record<
  DocumentFieldType,
  { label: string; color: string; icon: typeof SignatureIcon }
> = {
  signature: {
    label: "Assinatura",
    color: "border-primary bg-primary/10",
    icon: SignatureIcon,
  },
  initial: { label: "Rubrica", color: "border-info bg-info/10", icon: PenLine },
  date: { label: "Data", color: "border-warn bg-warn/10", icon: Calendar },
  text: { label: "Texto", color: "border-ok bg-ok/10", icon: TypeIcon },
};

interface DraftField extends DocumentFieldInput {
  key: string;
}

/**
 * Renders an uploaded PDF and lets the operator draw signature/initial/date/
 * text boxes on it. Entity-agnostic: the caller loads `fields` from wherever
 * (contract_fields, budget_fields) and persists them in `onSave`.
 */
export function PdfFieldEditor({
  sourcePdfUrl,
  signerLabels,
  fields: savedFields,
  fieldsLoading,
  onSave,
  saving,
}: {
  sourcePdfUrl: string;
  signerLabels: string[];
  fields: DocumentField[] | undefined;
  fieldsLoading: boolean;
  onSave: (fields: DocumentFieldInput[]) => void;
  saving: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState<
    { canvas: HTMLCanvasElement; width: number; height: number }[]
  >([]);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [fields, setFields] = useState<DraftField[]>([]);
  const [drawing, setDrawing] = useState<{
    page: number;
    startX: number;
    startY: number;
    x: number;
    y: number;
    w: number;
    h: number;
  } | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [addPage, setAddPage] = useState(1);

  useEffect(() => {
    if (!savedFields) return;
    setFields(
      savedFields.map((f, i) => ({
        key: `saved-${i}`,
        field_type: f.field_type,
        page_number: f.page_number,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        assigned_signer_index: f.assigned_signer_index,
        label: f.label ?? undefined,
      })),
    );
  }, [savedFields]);

  useEffect(() => {
    let cancelled = false;
    setLoadingPdf(true);
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ url: sourcePdfUrl }).promise;
        const rendered: { canvas: HTMLCanvasElement; width: number; height: number }[] = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.3 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvas, viewport }).promise;
          rendered.push({ canvas, width: viewport.width, height: viewport.height });
        }
        if (!cancelled) setPages(rendered);
      } catch (e) {
        toast.error("Erro ao carregar PDF: " + (e instanceof Error ? e.message : "desconhecido"));
      } finally {
        if (!cancelled) setLoadingPdf(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourcePdfUrl]);

  const handleMouseDown = useCallback((pageIndex: number, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const startX = (e.clientX - rect.left) / rect.width;
    const startY = (e.clientY - rect.top) / rect.height;
    setDrawing({ page: pageIndex + 1, startX, startY, x: startX, y: startY, w: 0, h: 0 });
    setSelectedKey(null);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!drawing) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const curX = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
      const curY = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
      setDrawing(
        (d) =>
          d && {
            ...d,
            x: Math.min(d.startX, curX),
            y: Math.min(d.startY, curY),
            w: Math.abs(curX - d.startX),
            h: Math.abs(curY - d.startY),
          },
      );
    },
    [drawing],
  );

  // Index 0 is always the admin (pre-signed automatically); a field the
  // operator draws by hand is almost always meant for a client, so it
  // defaults to the first real client slot instead of the admin.
  const defaultSignerIndex = signerLabels.length > 1 ? 1 : 0;

  const handleMouseUp = useCallback(() => {
    if (!drawing) return;
    if (drawing.w > 0.02 && drawing.h > 0.01) {
      const key = `new-${Date.now()}`;
      const field: DraftField = {
        key,
        field_type: "signature",
        page_number: drawing.page,
        x: drawing.x,
        y: drawing.y,
        width: drawing.w,
        height: drawing.h,
        assigned_signer_index: defaultSignerIndex,
      };
      setFields((prev) => [...prev, field]);
      setSelectedKey(key);
    }
    setDrawing(null);
  }, [drawing, defaultSignerIndex]);

  const updateField = (key: string, patch: Partial<DraftField>) => {
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  };

  const removeField = (key: string) => {
    setFields((prev) => prev.filter((f) => f.key !== key));
    if (selectedKey === key) setSelectedKey(null);
  };

  const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

  /** Caminho por teclado: campo padrão no canto superior esquerdo da página. */
  const addFieldOnPage = (pageNumber: number) => {
    const key = `new-${Date.now()}`;
    const onPage = fields.filter((f) => f.page_number === pageNumber).length;
    setFields((prev) => [
      ...prev,
      {
        key,
        field_type: "signature",
        page_number: pageNumber,
        // desloca a cada novo campo para não empilhar no mesmo lugar
        x: clamp01(0.1 + (onPage % 3) * 0.05),
        y: clamp01(0.1 + Math.floor(onPage / 3) * 0.08),
        width: 0.25,
        height: 0.06,
        assigned_signer_index: defaultSignerIndex,
      },
    ]);
    setSelectedKey(key);
    requestAnimationFrame(() =>
      containerRef.current?.querySelector<HTMLElement>(`[data-field-key="${key}"]`)?.focus(),
    );
  };

  /** Setas movem (Shift = passo maior), Alt+setas redimensionam, Delete remove. */
  const handleFieldKeyDown = (f: DraftField, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedKey(f.key);
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      removeField(f.key);
      return;
    }
    const step = e.shiftKey ? 0.05 : 0.01;
    const dir: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const d = dir[e.key];
    if (!d) return;
    e.preventDefault();
    setSelectedKey(f.key);
    if (e.altKey) {
      updateField(f.key, {
        width: clamp01(Math.max(0.02, f.width + d[0])),
        height: clamp01(Math.max(0.01, f.height + d[1])),
      });
    } else {
      updateField(f.key, {
        x: clamp01(Math.min(f.x + d[0], 1 - f.width)),
        y: clamp01(Math.min(f.y + d[1], 1 - f.height)),
      });
    }
  };

  const handleSave = () => {
    onSave(fields.map(({ key: _key, ...f }) => f));
  };

  if (loadingPdf || fieldsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Clique e arraste sobre o documento, ou use "Adicionar campo". Com o campo focado: setas
          movem (Shift = passo maior), Alt+setas redimensionam, Delete remove.
        </p>
        <div className="flex items-center gap-2">
          {pages.length > 1 && (
            <>
              <Label htmlFor="pdf-add-page" className="sr-only">
                Página do novo campo
              </Label>
              <Select value={String(addPage)} onValueChange={(v) => setAddPage(Number(v))}>
                <SelectTrigger id="pdf-add-page" className="h-8 w-28 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pages.map((_, i) => (
                    <SelectItem key={i} value={String(i + 1)}>
                      Página {i + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => addFieldOnPage(addPage)}
            disabled={pages.length === 0}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Adicionar campo
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            Salvar campos
          </Button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="max-h-[70vh] space-y-3 overflow-y-auto rounded-lg border bg-muted/20 p-3"
      >
        {pages.map((p, pageIndex) => (
          // Superfície de desenho por mouse. A alternativa por teclado é o botão
          // "Adicionar campo" + setas/Delete no campo focado (handleFieldKeyDown).
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            key={pageIndex}
            className="relative mx-auto cursor-crosshair select-none"
            style={{ width: p.width, height: p.height }}
            onMouseDown={(e) => handleMouseDown(pageIndex, e)}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <img
              src={p.canvas.toDataURL()}
              alt={`Página ${pageIndex + 1}`}
              className="pointer-events-none absolute inset-0 h-full w-full border shadow-sm"
              draggable={false}
            />
            {drawing && drawing.page === pageIndex + 1 && (
              <div
                className="pointer-events-none absolute border-2 border-primary bg-primary/10"
                style={{
                  left: `${drawing.x * 100}%`,
                  top: `${drawing.y * 100}%`,
                  width: `${drawing.w * 100}%`,
                  height: `${drawing.h * 100}%`,
                }}
              />
            )}
            {fields
              .filter((f) => f.page_number === pageIndex + 1)
              .map((f) => {
                const meta = fieldTypeMeta[f.field_type];
                const Icon = meta.icon;
                return (
                  <div
                    key={f.key}
                    className={`absolute border-2 ${meta.color} group flex cursor-pointer items-center justify-center text-[10px] font-medium`}
                    style={{
                      left: `${f.x * 100}%`,
                      top: `${f.y * 100}%`,
                      width: `${f.width * 100}%`,
                      height: `${f.height * 100}%`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedKey(f.key);
                    }}
                    role="button"
                    tabIndex={0}
                    data-field-key={f.key}
                    aria-label={`Campo ${meta.label} — ${signerLabels[f.assigned_signer_index] ?? `Signatário ${f.assigned_signer_index + 1}`}. Setas movem, Alt+setas redimensionam, Delete remove.`}
                    aria-pressed={selectedKey === f.key}
                    onKeyDown={(e) => handleFieldKeyDown(f, e)}
                  >
                    <Icon className="mr-1 h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {signerLabels[f.assigned_signer_index] ??
                        `Signatário ${f.assigned_signer_index + 1}`}
                    </span>
                    <button
                      type="button"
                      aria-label="Remover campo"
                      className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 focus-visible:opacity-100 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeField(f.key);
                      }}
                    >
                      <Trash2 className="h-2.5 w-2.5" />
                    </button>
                  </div>
                );
              })}
          </div>
        ))}
      </div>

      {selectedKey &&
        (() => {
          const field = fields.find((f) => f.key === selectedKey);
          if (!field) return null;
          return (
            <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Tipo do campo</p>
                <Select
                  value={field.field_type}
                  onValueChange={(v) =>
                    updateField(selectedKey, { field_type: v as DocumentFieldType })
                  }
                >
                  <SelectTrigger className="h-8 w-36 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(fieldTypeMeta).map(([v, m]) => (
                      <SelectItem key={v} value={v}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Signatário</p>
                <Select
                  value={String(field.assigned_signer_index)}
                  onValueChange={(v) =>
                    updateField(selectedKey, { assigned_signer_index: Number(v) })
                  }
                >
                  <SelectTrigger className="h-8 w-44 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(signerLabels.length > 0
                      ? signerLabels
                      : ["Signatário 1", "Signatário 2", "Signatário 3"]
                    ).map((label, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(
                [
                  ["x", "X (%)"],
                  ["y", "Y (%)"],
                  ["width", "Largura (%)"],
                  ["height", "Altura (%)"],
                ] as const
              ).map(([prop, label]) => (
                <div key={prop} className="space-y-1">
                  <Label htmlFor={`pdf-field-${prop}`} className="text-xs text-muted-foreground">
                    {label}
                  </Label>
                  <Input
                    id={`pdf-field-${prop}`}
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    className="h-8 w-20 text-xs"
                    value={Math.round(field[prop] * 100)}
                    onChange={(e) =>
                      updateField(selectedKey, {
                        [prop]: clamp01(Number(e.target.value) / 100),
                      })
                    }
                  />
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs text-destructive"
                onClick={() => removeField(selectedKey)}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remover campo
              </Button>
            </div>
          );
        })()}
    </div>
  );
}
